import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { shuffle } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { TeamSliderBlock } from '../../@types/statamic'
import { getSpecificTeamMembers, getTeamMembers } from '../../AdminApiClient'
import Title from '../../components/elements/Title'
import Container from '../../components/layout/Container'
import { PageInfo } from '@/src/@types/models'
import Button from '@/src/components/core/Button'
import TeamMember from '@/src/components/elements/TeamMember'

type TeamSliderBlockProps = Awaited<ReturnType<typeof getStaticProps>>

export function getTitle({ title }: TeamSliderBlock) {
  return title
}

const INSTRUCTOR_LIMIT = 2

export async function getStaticProps({
  title,
  text,
  roles,
  locations,
  opt_team_members,
  show_all,
}: TeamSliderBlock) {
  let team
  if (opt_team_members && opt_team_members.length > 0) {
    team = await getSpecificTeamMembers(
      opt_team_members.map((team_member) => team_member.value)
    )
  } else {
    team = await getTeamMembers(
      roles.map((role) => role.value),
      locations?.map((location) => location.value)
    )
    if (team.length === 0) {
      team = (
        await getTeamMembers(
          roles.map((role) => role.value),
          undefined
        )
      ).slice(0, 3)
    }
  }

  team = shuffle(team)

  if (show_all) {
    text = text
      ?.replaceAll('{instructor_count}', team.length.toString())
      .replaceAll(
        '{instructor_names}',
        team
          .slice(0, -1)
          .map((member) => member.name)
          .join(', ') +
          ' und ' +
          team.slice(-1)[0].name
      )
    return {
      team,
      title,
      text: text ?? null,
      locationIds: locations?.map((location) => location.value) ?? [],
    }
  }

  const instructors = team.filter((member) =>
    member.roles.includes('Kursinstruktor:innen')
  )
  const rest = team.filter(
    (member) => !member.roles.includes('Kursinstruktor:innen')
  )

  const tempRestCount = Math.min(9 - INSTRUCTOR_LIMIT, rest.length)
  const instructorCount = Math.min(9 - tempRestCount, instructors.length)
  const restCount = Math.max(tempRestCount, 9 - instructorCount)

  team = shuffle([
    ...instructors.slice(0, instructorCount),
    ...rest.slice(0, restCount),
  ])

  text = text
    ?.replaceAll('{instructor_count}', team.length.toString())
    .replaceAll(
      '{instructor_names}',
      team.length === 1
        ? `${team[0].name}`
        : team
            .slice(0, -1)
            .map((member) => member.name)
            .join(', ') +
            ' und ' +
            team.slice(-1)[0].name
    )

  return {
    team,
    title,
    text: text ?? null,
    locationIds: locations?.map((location) => location.value) ?? [],
  }
}

export default function Component({
  pageInfo,
  ...teamSliderBlock
}: TeamSliderBlockProps & {
  pageInfo: PageInfo
}) {
  return (
    <div
      className={clsx({
        'md:pb-12 lg:pb-16': !pageInfo.newSpacing,
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <Container fullPage className="lg:max-w-container">
        <div className="lg:hidden">
          <Mobile {...teamSliderBlock} />
        </div>
        <div className="hidden lg:block">
          <Desktop {...teamSliderBlock} />
        </div>
      </Container>
    </div>
  )
}

function Mobile({ title, text, team, locationIds }: TeamSliderBlockProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel()
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  const prev = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const next = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <>
      <div className="mx-5">
        <Title style={2}>{title}</Title>
        {text && (
          <p className="mt-5 whitespace-pre-wrap text-gray-800">{text}</p>
        )}
      </div>
      <div className="mt-6 overflow-hidden pb-10" ref={emblaRef}>
        <ul className="flex select-none">
          {team.map((member) => (
            <div className="ml-5 min-w-0 flex-[0_0_calc(100%-40px-20px)] last:mr-5 md:flex-[0_0_calc((100%-40px-40px)/2)]">
              <TeamMember
                key={member.slug}
                member={member}
                locationIds={locationIds}
              />
            </div>
          ))}
        </ul>
      </div>
      {!(prevDisabled && nextDisabled) && (
        <div className="mx-5 mt-6 flex shrink-0 justify-end space-x-3">
          <Button
            size="icon-small"
            icon={<CaretLeft weight="bold" />}
            onClick={prev}
            version="gray-outlined"
            disabled={prevDisabled}
          />
          <Button
            size="icon-small"
            icon={<CaretRight weight="bold" />}
            onClick={next}
            version="gray-outlined"
            disabled={nextDisabled}
          />
        </div>
      )}
    </>
  )
}

function Desktop({ title, text, team, locationIds }: TeamSliderBlockProps) {
  const [shownTeamMembers, setShownTeamMembers] = useState(team.slice(0, 6))

  return (
    <div className="mx-6">
      <Title style={2}>{title}</Title>
      {text && (
        <p className="mt-6 whitespace-pre-wrap text-gray-800 lg:max-w-2/3 lg:text-lg">
          {text}
        </p>
      )}
      <ul className="mt-10 grid w-full grid-cols-1 gap-x-8 gap-y-[88px] pb-8 sm:grid-cols-2 lg:grid-cols-3">
        {shownTeamMembers.map((member) => (
          <TeamMember
            key={member.slug}
            member={member}
            locationIds={locationIds}
          />
        ))}
      </ul>
      {shownTeamMembers.length !== team.length && (
        <div className="mt-8">
          <button
            onClick={() => {
              setShownTeamMembers(team)
            }}
            className="font-bold text-purple-600"
          >
            Ganzes Team anzeigen
          </button>
        </div>
      )}
    </div>
  )
}
